import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { apiConnector } from "../apiConnector";
import { endpoint } from "../apis";
import Cookies from "js-cookie";

// Async thunk for initiating payment (POST method)
export const generatePaymentLink = createAsyncThunk(
  'payment/generatePaymentLink',
  async ({ company_name, amount, email, phone_number, campaign_id }, { rejectWithValue }) => {
    try {
      const token = Cookies.get("authToken");
      const payload = {
        action: "initiate",
        company_name,
        amount,
        email,
        phone_number,
        campaign_id
      };

      const response = await apiConnector(
        "post",
        endpoint.PAYMENT_INITIATE,
        payload,
        { Authorization: `Bearer ${token}` }
      );
      
      // Parse the response - it comes as a tuple with merchant_order_id and payment URL
      // The response might be in different formats, handle both possibilities
      let merchant_order_id, payment_url;
      
      if (Array.isArray(response.data)) {
        // If it's an array, destructure it
        [merchant_order_id, payment_url] = response.data;
      } else if (typeof response.data === 'string') {
        // If it's a string, it might be a stringified tuple
        try {
          const matches = response.data.match(/['"]([^'"]+)['"],\s*['"]([^'"]+)['"]/);
          if (matches && matches.length >= 3) {
            merchant_order_id = matches[1];
            payment_url = matches[2];
          }
        } catch (e) {
          console.error('Error parsing payment response:', e);
        }
      }
      
      // Ensure merchant_order_id is a clean string without any parentheses or quotes
      if (merchant_order_id && typeof merchant_order_id === 'string') {
        merchant_order_id = merchant_order_id.replace(/[()'"]/g, '').trim();
      }
      
      
      
      return {
        payment_id: merchant_order_id,
        payment_url
      };
    } catch (error) {
      if (error?.response?.data === "('Token has been expired',)") {
        localStorage.clear();
        const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'http://www.katalisia.com';
        window.location.href = frontendUrl;
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(error.response?.data || "Failed to generate payment link");
      }
      return rejectWithValue(error.response?.data || "Failed to generate payment link");
    }
  }
);

// Async thunk for checking payment status (POST method)
export const checkPaymentStatus = createAsyncThunk(
  'payment/checkPaymentStatus',
  async ({ merchant_order_id }, { rejectWithValue }) => {
    try {
      const token = Cookies.get("authToken");
      
      // Use static merchant_order_id for testing if needed
      const staticMerchantId = "902c7700-e8f8-48bb-9517-5986f3da3fed";
      
      // Clean the merchant_order_id to ensure it's properly formatted
      const cleanMerchantOrderId = typeof merchant_order_id === 'string' 
        ? merchant_order_id.replace(/[()'"]/g, '').trim() 
        : merchant_order_id;
      
      // For testing, use the static ID
      const finalMerchantId = cleanMerchantOrderId;
      
      // Format exactly as in Postman - sending as JSON body
      const payload = {
        action: "status",
        merchant_order_id: finalMerchantId
      };
      
      
      
      // Send as POST with JSON body to match Postman
      const response = await apiConnector(
        "post",
        endpoint.PAYMENT_STATUS,
        payload,
        { Authorization: `Bearer ${token}` }
      );
      
      
      
      if (response.data && response.data.Response) {
        // Return the status from the response
        return {
          status: response.data.Response.status.toLowerCase(),
          payment_id: response.data.Response.merchant_order_id,
          timestamp: response.data.Response.timestamp
        };
      } else {
        console.error('Unexpected response format:', response.data);
        return rejectWithValue(response.data?.error || 'Unexpected response format');
      }
    } catch (error) {
      console.error('Payment status check error:', error.response?.data || error.message);
      if (error?.response?.data === "('Token has been expired',)") {
        localStorage.clear();
        const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'http://www.katalisia.com';
        window.location.href = frontendUrl;
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(error.response?.data || "Failed to check payment status");
      }
      return rejectWithValue(error.response?.data || "Failed to check payment status");
    }
  }
);

// Handler function that can be used directly in components
export const paymentHandler = ({ actionType, data, showToast = true }) => {
  return async (dispatch) => {
    try {
      let result;

      console.log("Payment Data", data)

      switch (actionType) {
        case 'generateLink':
          result = await dispatch(generatePaymentLink(data)).unwrap();
          if (showToast) toast.success('Payment link generated successfully');
          break;
        case 'checkStatus':
          result = await dispatch(checkPaymentStatus(data)).unwrap();
          break;
        default:
          throw new Error('Invalid action type');
      }

      return result;
    } catch (error) {
      console.error('Payment operation failed:', error);
      if (showToast) {
        toast.error(error.message || 'Payment operation failed');
      }
      throw error;
    }
  };
};
